import { CartUpdateEvent } from '@theme/events';
import { morphSection, buildSectionSelector } from '@theme/section-renderer';
import { replaceCartLine, addCartItem } from './zentus-cart-transactions.js';
import { cartMutationGate, flushCartQuantityChanges, runCartMutation, syncCartMutationUI, cartSectionMatches } from '@theme/zentus-cart-gate';
import { attachOptionGestures, gesturesIdle } from '@theme/zentus-cart-gestures';

let busy = false;
const requests = new WeakMap();
const stripRequests = new WeakMap();
const gestureRails = new WeakSet();
const root = window.Shopify?.routes?.root || '/';
const sectionIds = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))].slice(0, 5);

async function api(path, body) {
  const response = await fetch(`${root}${path}`, {
    method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.errors || data.status >= 400) {
    const error = new Error(typeof data.description === 'string' ? data.description : Theme.translations.cart_update_error);
    error.serverMessage = typeof data.description === 'string' ? data.description : null;
    throw error;
  }
  return data;
}

function syncLock() {
  syncCartMutationUI(document, cartMutationGate, `${root}cart`, Theme.translations.cart_update_error);
}
cartMutationGate.subscribe(syncLock);

function update(cart, renderedSections) {
  document.dispatchEvent(new CartUpdateEvent(cart, 'zentus-cart', {
    source: 'cart-items-component', itemCount: cart.item_count, sections: cart.sections, renderedSections,
  }));
}

async function fetchSections(ids) {
  const url = new URL(window.location.href);
  url.searchParams.delete('section_id');
  url.searchParams.set('sections', ids.join(','));
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error('Cart sections could not be loaded.');
  return response.json();
}

async function recoverCart() {
  const cart = await api('cart.js');
  return { ...cart, sections: await fetchSections(sectionIds()) };
}

async function renderCart(cart) {
  const ids = sectionIds();
  if (!ids.length) throw new Error('Cart section is unavailable.');
  let sections = cart.sections;
  if (ids.some(id => typeof sections?.[id] !== 'string')) sections = await fetchSections(ids);
  for (const id of ids) {
    const doc = new DOMParser().parseFromString(sections[id], 'text/html');
    const section = doc.getElementById(buildSectionSelector(id));
    const component = [...(section?.querySelectorAll('cart-items-component') || [])].find(item => item.dataset.sectionId === id);
    if (!cartSectionMatches(cart, component)) throw new Error('Cart section did not match the confirmed cart.');
  }
  for (const id of ids) {
    const current = [...document.querySelectorAll('cart-items-component')].find(item => item.dataset.sectionId === id);
    await morphSection(id, sections[id], current?.hasAttribute('data-drawer') ? 'hydration' : 'full');
  }
  update({ ...cart, sections }, ids);
}

function reportMutationError(container, error) {
  if (cartMutationGate.blocked) { syncLock(); return; }
  if (container.isConnected) { message(container, error, error.uncertain); return; }
  // A verified recovery may replace the editor that initiated the operation.
  const output = document.createElement('p');
  output.className = 'zc-editor-error'; output.dataset.zcFeedback = ''; output.setAttribute('role', 'alert');
  output.textContent = (error.uncertain ? container.dataset.uncertainMessage : container.dataset.errorMessage) || Theme.translations.cart_update_error;
  document.querySelector('cart-items-component')?.before(output);
}

function beginMutation() {
  busy = true;
  document.querySelectorAll('[data-zc-feedback]').forEach(output => output.remove());
  flushCartQuantityChanges(document);
}

function message(container, error, uncertain = false) {
  const output = container.querySelector('[data-zc-editor-error], [data-zc-offer-error]');
  if (!output) return;
  output.textContent = error?.serverMessage || (uncertain ? container.dataset.uncertainMessage : container.dataset.errorMessage) || container.dataset.zcError || Theme.translations.cart_update_error;
  output.hidden = false;
}

async function loadEditor(row, options, focusValue) {
  const host = row.querySelector('[data-zc-editor-host]');
  if (!host) return;
  requests.get(host)?.abort();
  const controller = new AbortController();
  requests.set(host, controller);
  host.hidden = false;
  host.setAttribute('aria-busy', 'true');
  const pendingControls = [...host.querySelectorAll('[data-zc-option], [data-zc-apply], [data-zc-add-offer]')].filter(control => !control.disabled);
  pendingControls.forEach(control => { control.disabled = true; });
  row.querySelectorAll('[data-zc-edit]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
  const url = new URL(row.dataset.productUrl, window.location.origin);
  url.searchParams.set('section_id', 'zentus-cart-variant');
  if (options) url.searchParams.set('option_values', options.join(','));
  else if (row.dataset.variantId) url.searchParams.set('variant', row.dataset.variantId);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error('Option request failed');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const editor = doc.querySelector('[data-zc-editor]');
    if (!editor) throw new Error('Option response unavailable');
    await gesturesIdle(host);
    if (controller.signal.aborted || !row.isConnected || host.hidden) return;
    if (row.matches('[data-zc-offer]') || row.dataset.addLabel) {
      const action = editor.querySelector('[data-zc-apply]');
      action.removeAttribute('data-zc-apply');
      action.setAttribute('data-zc-add-offer', '');
      editor.querySelector('[data-zc-action-label]').textContent = row.dataset.addLabel;
    }
    host.replaceChildren(editor);
    if (row.dataset.zcUncertain === 'true') {
      editor.querySelector('[data-zc-apply], [data-zc-add-offer]').disabled = true;
      message(editor, null, true);
    }
    initializeOptionGestures(editor);
    const focus = focusValue ? editor.querySelector(`[data-zc-option="${CSS.escape(focusValue)}"]:not([data-zc-clone])`) : editor.querySelector('[aria-pressed="true"], [data-zc-cancel]');
    focus?.focus({ preventScroll: true });
    if (!options && !focusValue) revealDrawerEditor(editor);
  } catch (error) {
    if (error.name !== 'AbortError') {
      if (!host.querySelector('[data-zc-editor-error]')) {
        const output = document.createElement('p'); output.dataset.zcEditorError = ''; output.setAttribute('role', 'alert'); host.append(output);
      }
      message(host, error);
    }
  } finally {
    if (requests.get(host) === controller) {
      host.removeAttribute('aria-busy');
      pendingControls.forEach(control => { if (control.isConnected) control.disabled = false; });
    }
  }
}

function revealDrawerEditor(editor) {
  const scroller = editor.closest('.cart-drawer__items');
  if (!scroller || !scroller.clientHeight) return;
  const viewportTop = scroller.getBoundingClientRect().top + scroller.clientTop;
  const bounds = editor.getBoundingClientRect();
  const delta = bounds.height > scroller.clientHeight || bounds.top < viewportTop
    ? bounds.top - viewportTop
    : Math.max(0, bounds.bottom - viewportTop - scroller.clientHeight);
  if (delta) scroller.scrollTop = Math.max(0, scroller.scrollTop + delta);
}

function initializeOptionGestures(container) {
  container.querySelectorAll('[data-zc-gesture]').forEach(rail => {
    if (gestureRails.has(rail)) return;
    gestureRails.add(rail);
    attachOptionGestures(rail, {
      wheelScope: rail.closest('[data-zc-editor-host]') || rail,
      isLocked: () => busy || cartMutationGate.busy || cartMutationGate.blocked || rail.closest('[data-zc-editor-host]')?.getAttribute('aria-busy') === 'true',
      onSelect: direction => rail.closest('[data-zc-option-position]')?.querySelector(`[data-zc-direction="${direction}"]`)?.click(),
      // The photo strip commits the colour left in the centre through the same path as a tap.
      onCommit: option => {
        const host = rail.closest('[data-zc-editor-host]');
        option.click();
        if (host?.getAttribute('aria-busy') !== 'true') return false;
        stripRequests.set(host, requests.get(host));
        return true;
      },
      // Scrolling on cancels that commit's pending reload, so the strip never jumps back.
      onMove: () => {
        const host = rail.closest('[data-zc-editor-host]');
        const request = requests.get(host);
        if (request && stripRequests.get(host) === request) request.abort();
      },
    });
    const selected = rail.querySelector('[aria-pressed="true"]');
    if (rail.dataset.zcGesture === 'swatches' && selected) rail.scrollLeft = Math.max(0, selected.offsetLeft - rail.offsetLeft - (rail.clientWidth - selected.offsetWidth) / 2);
  });
}

function closeEditor(row) {
  const host = row.querySelector('[data-zc-editor-host]');
  requests.get(host)?.abort();
  if (host) host.hidden = true;
  row.querySelectorAll('[data-zc-edit]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  row.querySelector('[data-zc-edit]')?.focus({ preventScroll: true });
}

const activatedUtilities = new WeakSet();

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const utility = target?.closest('[data-zc-utility] > summary')?.parentElement;
  if (utility && !utility.open) activatedUtilities.add(utility);
  const button = target?.closest('[data-zc-edit], [data-zc-cancel], [data-zc-option], [data-zc-apply], [data-zc-add-offer], [data-zc-dismiss-offer]');
  if (!button) return;
  event.preventDefault();
  if (busy || cartMutationGate.busy || cartMutationGate.blocked || button.disabled) return;
  const row = button.closest('[data-zc-line]') || button.closest('[data-zc-offer]');
  if (button.matches('[data-zc-edit]') && row) {
    const host = row.querySelector('[data-zc-editor-host]');
    if (!host.hidden) closeEditor(row); else await loadEditor(row);
  } else if (button.matches('[data-zc-cancel]') && row) {
    closeEditor(row);
  } else if (button.matches('[data-zc-option]') && row) {
    const editor = button.closest('[data-zc-editor]');
    if (row.querySelector('[data-zc-editor-host]')?.getAttribute('aria-busy') === 'true' || button.getAttribute('aria-pressed') === 'true') return;
    const options = editor.dataset.selectedOptions.split(',');
    options[Number(button.closest('[data-zc-option-position]').dataset.zcOptionPosition) - 1] = button.dataset.zcOption;
    await loadEditor(row, options, button.dataset.zcOption);
  } else if (button.matches('[data-zc-apply]') && row) {
    const editor = button.closest('[data-zc-editor]');
    if (row.querySelector('[data-zc-editor-host]')?.getAttribute('aria-busy') === 'true' || editor.querySelector('[data-zc-pending]') || editor.dataset.available !== 'true' || !editor.dataset.variantId) return;
    const key = row.dataset.key;
    const variantId = Number(editor.dataset.variantId);
    beginMutation();
    editor.setAttribute('aria-busy', 'true');
    try {
      await runCartMutation(cartMutationGate, {
        mutate: () => {
          const currentRow = [...document.querySelectorAll('[data-zc-line]')].find(item => item.dataset.key === key);
          if (!currentRow) throw new Error('Cart changed. Review the current items.');
          return replaceCartLine(api, {
            key, variantId, quantity: Number(currentRow.dataset.quantity),
            sections: sectionIds(), sectionsUrl: window.location.pathname,
          });
        }, render: renderCart, recover: recoverCart,
      });
      document.querySelector(`[data-zc-line][data-variant-id="${variantId}"] [data-zc-edit]`)?.focus({ preventScroll: true });
    } catch (error) {
      reportMutationError(editor, error);
    } finally {
      busy = false; editor.removeAttribute('aria-busy');
    }
  } else if (button.matches('[data-zc-dismiss-offer]')) {
    const offer = button.closest('[data-zc-offer]');
    try { sessionStorage.setItem(`zentus-cart-offer-${offer.dataset.productId}`, 'hidden'); } catch { /* Storage is optional. */ }
    document.querySelectorAll('[data-zc-offer]').forEach((other) => { if (other.dataset.productId === offer.dataset.productId) other.hidden = true; });
  } else if (button.matches('[data-zc-add-offer]')) {
    const offer = button.closest('[data-zc-offer]');
    const editor = button.closest('[data-zc-editor]');
    const variantId = Number(editor?.dataset.variantId);
    if (offer.querySelector('[data-zc-editor-host]')?.getAttribute('aria-busy') === 'true' || editor?.querySelector('[data-zc-pending]') || editor?.dataset.available !== 'true' || Number(editor.dataset.price) <= 0 || !Number.isSafeInteger(variantId) || variantId <= 0) return;
    beginMutation();
    editor.setAttribute('aria-busy', 'true');
    try {
      await runCartMutation(cartMutationGate, {
        mutate: () => addCartItem(api, { variantId, sections: sectionIds(), sectionsUrl: window.location.pathname }),
        render: renderCart, recover: recoverCart,
      });
    } catch (error) {
      reportMutationError(editor, error);
    } finally { busy = false; editor.removeAttribute('aria-busy'); }
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || busy || !(event.target instanceof Element)) return;
  const row = event.target.closest('[data-zc-line]') || event.target.closest('[data-zc-offer]');
  if (row && !row.querySelector('[data-zc-editor-host]')?.hidden) {
    event.preventDefault(); event.stopPropagation(); closeEditor(row);
  }
}, true);

document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-zc-utility]')) return;
  const activated = activatedUtilities.delete(details);
  if (!details.open) return;
  details.closest('[data-zc-utilities]')?.querySelectorAll('[data-zc-utility]').forEach((other) => { if (other !== details) other.open = false; });
  if (activated) requestAnimationFrame(() => requestAnimationFrame(async () => {
    const animations = details.closest('[data-zc-utilities]')?.getAnimations({ subtree: true }) || [];
    await Promise.allSettled(animations.map(animation => animation.finished));
    if (!details.isConnected || !details.open) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    const scroller = details.closest('.cart-drawer__items');
    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior });
    } else {
      details.querySelector('.details-content')?.scrollIntoView({ block: 'nearest', behavior });
    }
  }));
}, true);

function restoreDismissedOffers() {
  syncLock();
  const template = document.querySelector('[data-zentus-drawer-template]');
  if (template instanceof HTMLTemplateElement) {
    document.querySelectorAll('[data-zc-shipping-bar-slot]').forEach((slot) => {
      if (!slot.firstElementChild) slot.append(template.content.cloneNode(true));
    });
  }
  // The legacy shipping widget observes the whole body, including its own
  // rendering, causing a cart.js fetch loop. These cart surfaces already emit
  // cart:update / discount:update / quantity-selector:update; keep those native
  // listeners and the widget's rendering/rules, and stop only its DOM fallback.
  document.querySelectorAll('[data-zc-shipping-bar-slot] zentus-shipping-bar, .cart-drawer__summary zentus-shipping-bar').forEach((bar) => {
    if (bar.dataset.surface === 'drawer' && bar._observer instanceof MutationObserver) {
      bar._observer.disconnect();
      bar._observer = null;
    }
  });
  document.querySelectorAll('[data-zc-offer]').forEach((offer) => {
    try { if (sessionStorage.getItem(`zentus-cart-offer-${offer.dataset.productId}`) === 'hidden') offer.hidden = true; } catch { /* Storage is optional. */ }
  });
}
new MutationObserver(restoreDismissedOffers).observe(document.body, { childList: true, subtree: true });
restoreDismissedOffers();
